/**
 * The reference regex highlighter — a deliberately minimal, synchronous,
 * language-agnostic tokenizer: strings + numbers, a fixed keyword list, and
 * call-position identifiers. Everything else passes through as plain text.
 *
 * Pure data out (no React, no imports): the component renders tokens as text
 * nodes, so joining token texts always reproduces the input verbatim — that
 * round-trip is the no-HTML-injection guarantee and is pinned by
 * tests/code-block-highlight.test.ts.
 */

export type HighlightTokenKind =
  | 'string'
  | 'number'
  | 'keyword'
  | 'call'
  | 'plain'

export type HighlightToken = {
  text: string
  kind: HighlightTokenKind
}

const KEYWORDS = new Set([
  'import',
  'from',
  'export',
  'default',
  'async',
  'function',
  'const',
  'let',
  'var',
  'await',
  'return',
  'if',
  'else',
  'for',
  'while',
  'new',
  'throw',
  'try',
  'catch',
  'null',
  'true',
  'false',
  'undefined',
])

// Alternation order matters: strings swallow everything they contain, and the
// keyword branch sits before the call branch so `if (` stays a keyword.
const TOKEN_PATTERN =
  /("(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'|`[^`]*`|\b\d+(?:\.\d+)?\b|\b(?:import|from|export|default|async|function|const|let|var|await|return|if|else|for|while|new|throw|try|catch|null|true|false|undefined)\b|[A-Za-z_$][\w$]*(?=\s*\())/g

function classify(text: string): HighlightTokenKind {
  if (/^["'`]/.test(text)) {
    return 'string'
  }
  if (/^\d/.test(text)) {
    return 'number'
  }
  if (KEYWORDS.has(text)) {
    return 'keyword'
  }
  return 'call'
}

/** Tokenize one line. An empty line yields an empty token list. */
export function highlightLine(line: string): Array<HighlightToken> {
  const tokens: Array<HighlightToken> = []
  let lastIndex = 0
  for (const match of line.matchAll(TOKEN_PATTERN)) {
    const index = match.index ?? 0
    const text = match[0]
    if (index > lastIndex) {
      tokens.push({ text: line.slice(lastIndex, index), kind: 'plain' })
    }
    tokens.push({ text, kind: classify(text) })
    lastIndex = index + text.length
  }
  if (lastIndex < line.length) {
    tokens.push({ text: line.slice(lastIndex), kind: 'plain' })
  }
  return tokens
}

/** Tokenize a whole snippet, one token list per line. */
export function highlightCode(code: string): Array<Array<HighlightToken>> {
  return code.split('\n').map(highlightLine)
}
